import { useCallback, useEffect, useMemo } from 'react';
import type {
  AgentChatPanelProps,
  ChatUser,
} from '@amzn/gsce-shared-react-lib/components';
import { useCurrentUser } from '../../shared/auth';
import { INTERACTION_TYPES, trackInteraction } from '../../shared/observability';
import { AstraMarkdown } from './AstraMarkdown';
import { ASTRA_AGENT_NAME, ASTRA_AI_POLICY_URL } from './constants';
import { useAstra } from './conversation/AstraConversationProvider';
import { ASTRA_SUGGESTED_PROMPTS } from './suggestedPrompts';
import { ASTRA_ACTIONS, type AstraAction, type AstraPage } from './tracking';

/**
 * The panel props every Astra surface shares. Layout (`fullscreen`,
 * `dataTestId`) and the surface-specific controls (`onClose`,
 * `onToggleExpand`/`isExpanded`) are left to the caller.
 */
export type AstraPanelProps = Pick<
  AgentChatPanelProps,
  | 'agentName'
  | 'messages'
  | 'isLoading'
  | 'isResponding'
  | 'currentUser'
  | 'onSend'
  | 'onStop'
  | 'onNewConversation'
  | 'suggestedPrompts'
  | 'markdownRenderer'
  | 'aiPolicyUrl'
>;

export interface AstraPanelBindings {
  /** Spread onto `AgentChatPanel`; add the surface's own layout/close props. */
  readonly panelProps: AstraPanelProps;
  /** Emits an `action_triggered` event for this surface — for the caller's own controls. */
  readonly trackAction: (action: AstraAction) => void;
}

/** The panel reports a chip pick through `onSend`; recover the intent by matching the text. */
function isSuggestedPrompt(content: string): boolean {
  return ASTRA_SUGGESTED_PROMPTS.some((suggestion) => suggestion.prompt === content);
}

/**
 * Binds the Astra conversation (`useAstra`) and the signed-in user to the
 * shared `AgentChatPanel`'s props, with interaction tracking on every action.
 *
 * `AstraMessage` is field-for-field the panel's `ChatMessage`, so
 * `state.messages` passes straight through. The mobile `/chat` page and the
 * desktop floating panel both call this and differ only in the layout and
 * close/expand props they add on top — one mapping, two surfaces.
 *
 * Fires the `page_viewed` event for `page` on mount.
 */
export function useAstraPanelProps(page: AstraPage): AstraPanelBindings {
  const { alias, userMetadata } = useCurrentUser();
  const { state, isResponding, sendMessage, stopResponding, startNewConversation } =
    useAstra();

  useEffect(() => {
    trackInteraction({ type: INTERACTION_TYPES.pageViewed, page });
  }, [page]);

  const trackAction = useCallback(
    (action: AstraAction): void => {
      trackInteraction({ type: INTERACTION_TYPES.actionTriggered, page, action });
    },
    [page],
  );

  const currentUser = useMemo<ChatUser>(
    () => ({
      alias,
      ...(userMetadata.fullName ? { fullName: userMetadata.fullName } : {}),
    }),
    [alias, userMetadata.fullName],
  );

  const onSend = useCallback(
    (content: string): void => {
      trackAction(
        isSuggestedPrompt(content)
          ? ASTRA_ACTIONS.pickSuggestedPrompt
          : ASTRA_ACTIONS.sendMessage,
      );
      void sendMessage(content);
    },
    [sendMessage, trackAction],
  );

  const onStop = useCallback((): void => {
    trackAction(ASTRA_ACTIONS.stopResponse);
    stopResponding();
  }, [stopResponding, trackAction]);

  const onNewConversation = useCallback((): void => {
    trackAction(ASTRA_ACTIONS.newConversation);
    startNewConversation();
  }, [startNewConversation, trackAction]);

  const panelProps = useMemo<AstraPanelProps>(
    () => ({
      agentName: ASTRA_AGENT_NAME,
      messages: state.messages,
      isLoading: state.isLoading,
      isResponding,
      currentUser,
      onSend,
      onStop,
      onNewConversation,
      suggestedPrompts: ASTRA_SUGGESTED_PROMPTS,
      markdownRenderer: AstraMarkdown,
      aiPolicyUrl: ASTRA_AI_POLICY_URL,
    }),
    [
      state.messages,
      state.isLoading,
      isResponding,
      currentUser,
      onSend,
      onStop,
      onNewConversation,
    ],
  );

  return { panelProps, trackAction };
}
